import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { type FunctionComponent, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { LanguageCodeForJSA as LanguageCode } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { IconAsterisk } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import type { Language } from '~/ui/domain/language-picker/LanguagePicker';

const IndicatorGroup = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    gap: theme.spacing.l,
    justifyContent: 'space-between',
    minWidth: 150,
}));

const AIIconWrapper = styled.span(({ theme }) => ({
    alignItems: 'center',
    color: theme.colors.orange[600],
    display: 'flex',
    fontSize: theme.typography.body.S.regular.fontSize,
    height: theme.spacing.l,
    justifyContent: 'center',
    width: theme.spacing.l,
}));

type LanguageIndicatorProps = {
    readonly language: Language<LanguageCode>;
    readonly originLanguageId: LanguageCode | undefined;
    readonly originLanguageLabel: string;
    readonly originLanguageTooltip: string;
    readonly translatedLanguageTooltip: string;
};

export const LanguageWithIndicator: FunctionComponent<LanguageIndicatorProps> = ({
    language,
    originLanguageId,
    originLanguageLabel,
    originLanguageTooltip,
    translatedLanguageTooltip,
}) => {
    const hasOriginLanguage = is.nonEmptyString(originLanguageId);
    const isOriginLanguage = hasOriginLanguage && language.id === originLanguageId;
    const isTranslatedLanguage = hasOriginLanguage && language.id !== originLanguageId;
    const { translate } = useContext(ApplicationContext);

    return (
        <IndicatorGroup>
            {translate(`common.languages.${language.id}` as TranslationKey)}
            {isOriginLanguage && (
                <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                        <Badge variant="orange">{originLanguageLabel}</Badge>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side="top">{originLanguageTooltip}</Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip>
            )}
            {isTranslatedLanguage && (
                <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                        <AIIconWrapper>
                            <IconAsterisk />
                        </AIIconWrapper>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side="top">{translatedLanguageTooltip}</Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip>
            )}
        </IndicatorGroup>
    );
};
